import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';

import {Grid, screenSizeSmaller} from 'views/components';

import ClientFeed from './client_scenes/client_feed/ClientFeed';
import ClientSettings from './client_scenes/client_settings/ClientSettings';

import backend from 'utils/backend';

const ClientView = (props) => {
  const [refresh, setRefresh] = React.useState(1);

  const [clientSettingsView, setClientSettingsView] = React.useState('CONTACT');
  const [openDrawerView, setOpenDrawerView] = React.useState(false);

  const [clientData, setClientData] = React.useState();
  const [clientMessages, setClientMessage] = React.useState({});
  const [clientLogs, setClientLogs] = React.useState({});
  const [clientScheduleEvents, setClientScheduleEvents] = React.useState({});
  const [clientCareviewSettings, setClientCareviewSettings] = React.useState({});
  const [clientForms, setClientForms] = React.useState([]);
  const [clientPaymentSlip, setClientPaymentSlip] = React.useState({});
  const [clientPaymentPlan, setClientPaymentPlan] = React.useState(null);
  const [clientCareplan, setClientCareplan] = React.useState({});
  const [clientAuthentication, setClientAuthentication] = React.useState({});
  const [clientNotificationChannels, setClientNotificationChannels] = React.useState({});
  const [clientCommunication, setClientCommunication] = React.useState({});
  const [clientDialpadIntegration, setClientDialpadIntegration] = React.useState({});
  const [clientOwnerData, setClientOwnerData] = React.useState({});
  const [clientCallStatus, setClientCallStatus] = React.useState(null);

  const [clientMessagesLastEvaluatedKey, setClientMessagesLastEvaluatedKey] = React.useState(null);
  const [clientLogsLastEvaluatedKey, setClientLogsLastEvaluatedKey] = React.useState(null);
  const [clientPaymentSlipLastEvaluatedKey, setClientPaymentSlipLastEvaluatedKey] = React.useState(null);

  const [clientDataLoaded, setClientDataLoaded] = React.useState(false);
  const [clientMessagesLoading, setClientMessagesLoading] = React.useState(false);
  const [clientLogsLoading, setClientLogsLoading] = React.useState(false);
  const [clientPaymentSlipLoading, setClientPaymentSlipLoading] = React.useState(false);
  const [clientPaymentPlanLoading, setClientPaymentPlanLoading] = React.useState(false);
  const [ClientAuthenticationLoading, setClientAuthenticationLoading] = React.useState(false);
  const [clientNotificationLoading, setClientNotificationLoading] = React.useState(false);
  const [clientCommunicationLoading, setClientCommunicationLoading] = React.useState(false);
  const [clientDialpadIntegrationLoading, setClientDialpadIntegrationLoading] = React.useState(false);

  const [feedTab, setFeedTab] = React.useState('CAREPLAN');

  const progressWrap = props.statusProgressWrap;
  const clientRn = props.clientRn;
  const requester = props.requester;

  const requesterRn = requester.userRn;

  const showSettings = screenSizeSmaller('md');

  React.useEffect(() => {
    clientBackend.load.loadClientData(clientRn);
  }, [clientRn, refresh]);
  React.useEffect(() => {
    clientBackend.load.loadMessages(clientRn, null);
  }, [clientRn, refresh]);
  React.useEffect(() => {
    clientBackend.load.loadLogs(clientRn, null);
  }, [clientRn, refresh]);

  const clientBackend = {
    load: {
      loadClientData: async (clientRn) => {
        progressWrap(async () => {
          const output = await backend.client.getClient(clientRn, true, true, true, true, true, true);
          setClientData(output.clientDetail);
          setClientCareviewSettings(output.careview);
          setClientForms(output.forms);
          setClientDataLoaded(true);
          setClientCareplan(output.careplan);
          setClientCallStatus(output.callStatus?.introCallStatus);
          setClientOwnerData(output.ownerData);
        }, {debugString: 'CLIENT_LOAD'});
      },
      loadMessages: async (clientRn, clientMessagesLastEvaluatedKey) => {
        progressWrap(async () => {
          setClientMessagesLoading(true);
          const output = await backend.client.message.load(clientRn, clientMessagesLastEvaluatedKey, true, true);

          setClientMessage((messages) => {
            const newMessages = output.messages.reduce((result, message) => {
              return {
                ...result,
                [message.messageRn]: message,
              };
            }, {});

            return {
              ...messages,
              ...newMessages,
            };
          });
          setClientMessagesLastEvaluatedKey(output.lastEvaluatedKey);
          setClientMessagesLoading(false);
        }, {debugString: 'CLIENT_MESSAGE_LOAD'});
      },
      loadLogs: async (clientRn, clientLogsLastEvaluatedKey) => {
        progressWrap(async () => {
          setClientLogsLoading(true);
          const output = await backend.client.loadLogs(clientRn, clientLogsLastEvaluatedKey, true);

          setClientLogs((logs) => {
            const newLogs = output.logs.reduce((result, log) => {
              return {
                ...result,
                [log.clientLogRn]: log,
              };
            }, {});

            return {
              ...logs,
              ...newLogs,
            };
          });
          setClientLogsLastEvaluatedKey(output.lastEvaluatedKey);
          setClientLogsLoading(false);
        }, {debugString: 'CLIENT_LOGS_LOAD'});
      },
    },
    detail: {
      editDescription: async (newBody, onSuccess, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            const body = {
              description: {
                blob: newBody,
                editorType: 'MARKDOWN',
              },
            };
            const {clientDetail, updateLog} = await backend.client.updateDescription(clientRn, body);

            // Refresh
            setClientData(clientDetail);

            setClientLogs((logs) => {
              return {
                ...logs,
                [updateLog.clientLogRn]: updateLog,
              };
            });

            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {debugString: 'CLIENT_EDIT_DESCRIPTION'});
      },
      editPhoto: async (file, onSuccess, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            const type = file.type;
            const name = file.name;
            const body = {
              fileType: type,
              fileName: name,
            };
            const {clientDetail, signedUrl, updateLog} = await backend.client.updateFile(clientRn, body);

            const uploadResult = await backend.s3.upload(signedUrl, file);

            if (uploadResult.status === 200) {
              setClientData(clientDetail);
            } else {
              console.error('Upload failed with status', result.status);
            }

            setClientLogs((logs) => {
              return {
                ...logs,
                [updateLog.clientLogRn]: updateLog,
              };
            });

            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {debugString: 'CLIENT_EDIT_PHOTO'});
      },
      editStatus: async (status, onSuccess, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            const body = {
              status,
            };
            const {clientDetail, updateLog} = await backend.client.updateStatus(clientRn, body);

            // Refresh
            setClientData(clientDetail);

            setClientLogs((logs) => {
              return {
                ...logs,
                [updateLog.clientLogRn]: updateLog,
              };
            });

            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {debugString: 'CLIENT_EDIT_STATUS'});
      },
      editCallStatus: async (status, onSuccess, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            const body = {
              introCallStatus: status,
            };
            const {callStatus} = await backend.client.editCallStatus(clientRn, body);

            setClientCallStatus(callStatus.introCallStatus);

            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {debugString: 'CLIENT_EDIT_CALL_STATUS'});
      },
      editName: async (clientName, onSuccess, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            const body = {
              clientName,
            };
            const {clientDetail, updateLog} = await backend.client.updateName(clientRn, body);

            // Refresh
            setClientData(clientDetail);

            setClientLogs((logs) => {
              return {
                ...logs,
                [updateLog.clientLogRn]: updateLog,
              };
            });

            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {debugString: 'CLIENT_EDIT_NAME'});
      },
      editClientOwner: async (owner, onSuccess, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            const body = {
              owner: {
                userRn: owner,
              },
            };
            const {clientOwner} = await backend.client.editClientOwner(clientRn, body);

            // Refresh
            setClientOwnerData(clientOwner);

            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {debugString: 'CLIENT_EDIT_OWNER'});
      },
      addContact: async (type, detail, onSuccess, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            const body = {
              type,
              detail,
            };
            const {clientDetail, updateLog} = await backend.client.addContact(clientRn, body);

            // Refresh
            setClientData(clientDetail);

            setClientLogs((logs) => {
              return {
                ...logs,
                [updateLog.clientLogRn]: updateLog,
              };
            });

            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {debugString: 'CLIENT_ADD_CONTACT'});
      },
      removeContact: async (type, onSuccess, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            const body = {
              type,
            };
            const {clientDetail, updateLog} = await backend.client.removeContact(clientRn, body);

            // Refresh
            setClientData(clientDetail);

            setClientLogs((logs) => {
              return {
                ...logs,
                [updateLog.clientLogRn]: updateLog,
              };
            });

            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {debugString: 'CLIENT_REMOVE_CONTACT'});
      },
      addCaregiver: async (caregiverData, onSuccess, onError=()=>undefined) => {
        const {
          caregiverRn,
          status,
          recommendation,
          recommendationChips,
          availabilityOverlap,
          availability,
          availabilityLastUpdatedTime,
        } = caregiverData;

        progressWrap(async () => {
          try {
            if (recommendation) {
              const recommendationData = {
                recommendationId: clientRn,
                recommendation: {
                  blob: recommendation,
                  editorType: 'MARKDOWN',
                },
                recommendationChips,
                availabilityOverlap,
                availability,
                availabilityLastUpdatedTime,
                overwrite: true,
              };
              await backend.caregiver.addRecommendation(caregiverRn, recommendationData);
            }

            const body = {
              caregiverRn,
              status,
            };

            const {clientDetail, updateLog} = await backend.client.addCaregiver(clientRn, body);
            // Refresh
            setClientData(clientDetail);
            setClientLogs((logs) => {
              return {
                ...logs,
                [updateLog.clientLogRn]: updateLog,
              };
            });
            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {debugString: 'CLIENT_ADD_CAREGIVER'});
      },
      removeCaregiver: async (caregiverRn, onSuccess, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            const body = {
              caregiverRn,
            };
            const {clientDetail, updateLog} = await backend.client.removeCaregiver(clientRn, body);
            // Refresh
            setClientData(clientDetail);
            setClientLogs((logs) => {
              return {
                ...logs,
                [updateLog.clientLogRn]: updateLog,
              };
            });
            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {debugString: 'CLIENT_REMOVE_CAREGIVER'});
      },
      relationship: {
        add: async (relationship, onSuccess, onError=()=>undefined) => {
          progressWrap(async () => {
            try {
              const body = {
                relationship,
              };
              const {clientDetail, updateLog} = await backend.client.relationship.add(clientRn, body);

              // Refresh
              setClientData(clientDetail);

              setClientLogs((logs) => {
                return {
                  ...logs,
                  [updateLog.clientLogRn]: updateLog,
                };
              });

              onSuccess();
            } catch (error) {
              onError();
              console.error(error);
              throw new Error(error);
            }
          }, {debugString: 'CLIENT_ADD_RELATIONSHIP'});
        },
        edit: async (relationshipRn, relationship, onSuccess, onError=()=>undefined) => {
          progressWrap(async () => {
            try {
              const body = {
                relationship,
              };
              const {clientDetail, updateLog} = await backend.client.relationship.edit(clientRn, relationshipRn, body);

              // Refresh
              setClientData(clientDetail);

              setClientLogs((logs) => {
                return {
                  ...logs,
                  [updateLog.clientLogRn]: updateLog,
                };
              });

              onSuccess();
            } catch (error) {
              onError();
              console.error(error);
              throw new Error(error);
            }
          }, {debugString: 'CLIENT_EDIT_RELATIONSHIP'});
        },
        remove: async (relationshipRn, onSuccess, onError=()=>undefined) => {
          progressWrap(async () => {
            try {
              const {clientDetail, updateLog} = await backend.client.relationship.remove(clientRn, relationshipRn);

              // Refresh
              setClientData(clientDetail);

              setClientLogs((logs) => {
                return {
                  ...logs,
                  [updateLog.clientLogRn]: updateLog,
                };
              });

              onSuccess();
            } catch (error) {
              onError();
              console.error(error);
              throw new Error(error);
            }
          }, {debugString: 'CLIENT_REMOVE_RELATIONSHIP'});
        },
      },
    },
    messages: {
      create: async (messageType, newMessage, onSuccess, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            const body = {
              body: {
                blob: newMessage,
                editorType: 'MARKDOWN',
              },
              messageType: messageType,
              requester: {
                rn: requesterRn,
              },
            };
            const result = await backend.client.message.create(clientRn, body);

            setClientMessage((messages) => {
              return {
                ...messages,
                [result.messageRn]: result.message,
              };
            });

            if (messageType === 'ANNOUNCEMENTS') {
              setClientData((clientData) => {
                return {
                  ...clientData,
                  latestAnnouncementMessageRn: result.messageRn,
                  latestAnnouncementMessage: result.message,
                };
              });
            }
            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {debugString: 'CLIENT_MESSAGE_CREATE'});
      },
      update: async (messageRn, newMessage, onSuccess, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            const body = {
              body: {
                blob: newMessage,
                editorType: 'MARKDOWN',
              },
            };
            const result = await backend.client.message.update(clientRn, messageRn, body);

            setClientMessage((messages) => {
              messages[result.messageRn] = result.message;
              return {
                ...messages,
              };
            });

            if (clientData.latestAnnouncementMessageRn === result.messageRn) {
              setClientData((clientData) => {
                return {
                  ...clientData,
                  latestAnnouncementMessageRn: result.messageRn,
                  latestAnnouncementMessage: result.message,
                };
              });
            }

            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {
          debugString: 'CLIENT_MESSAGE_UPDATE',
        });
      },
      delete: async (messageRn, onSuccess, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            const result = await backend.client.message.delete(clientRn, messageRn);

            setClientMessage((messages) => {
              delete messages[result.messageRn];
              return {
                ...messages,
              };
            });

            if (clientData.latestAnnouncementMessageRn === result.messageRn) {
              setClientData((clientData) => {
                return {
                  ...clientData,
                  latestAnnouncementMessageRn: null,
                  latestAnnouncementMessage: null,
                };
              });
            }

            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {
          debugString: 'CLIENT_MESSAGE_DELETE',
        });
      },
      addEmoji: async (messageRn, emoji, userRn, onSuccess=()=>undefined, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            const body = {
              emoji: emoji,
              requester: {
                rn: userRn,
              },
            };
            const result = await backend.client.message.emoji.add(clientRn, messageRn, body);

            setClientMessage((messages) => {
              messages[result.messageRn] = result.message;
              return {
                ...messages,
              };
            });

            if (clientData.latestAnnouncementMessageRn === result.messageRn) {
              setClientData((clientData) => {
                return {
                  ...clientData,
                  latestAnnouncementMessageRn: result.messageRn,
                  latestAnnouncementMessage: result.message,
                };
              });
            }

            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {
          debugString: 'CLIENT_MESSAGE_EMOJI_ADD',
        });
      },
      removeEmoji: async (messageRn, emoji, userRn, onSuccess=()=>undefined, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            const body = {
              emoji: emoji,
              requester: {
                rn: userRn,
              },
            };
            const result = await backend.client.message.emoji.remove(clientRn, messageRn, body);

            setClientMessage((messages) => {
              messages[result.messageRn] = result.message;
              return {
                ...messages,
              };
            });

            if (clientData.latestAnnouncementMessageRn === result.messageRn) {
              setClientData((clientData) => {
                return {
                  ...clientData,
                  latestAnnouncementMessageRn: result.messageRn,
                  latestAnnouncementMessage: result.message,
                };
              });
            }

            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {
          debugString: 'CLIENT_MESSAGE_EMOJI_REMOVE',
        });
      },
    },
    schedule: {
      addFixedEvent: async (newEvent, onSuccess=()=>undefined, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            backend.caregiver.addClient(newEvent.caregiverData.caregiverRn, {clientRn});
            backend.client.addCaregiver(clientRn, {caregiverRn: newEvent.caregiverData.caregiverRn, status: 'ACTIVE'});

            await backend.client.schedule.addEvent(clientRn, newEvent);

            setClientScheduleEvents({});
            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {
          debugString: 'CLIENT_SCHEDULE_ADD_EVENT',
        });
      },
      addRecurrence: async (newEvent, onSuccess=()=>undefined, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            backend.caregiver.addClient(newEvent.caregiverData.caregiverRn, {clientRn});
            backend.client.addCaregiver(clientRn, {caregiverRn: newEvent.caregiverData.caregiverRn, status: 'ACTIVE'});

            await backend.client.schedule.addRecurrence(clientRn, newEvent);

            setClientScheduleEvents({});
            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {
          debugString: 'CLIENT_SCHEDULE_ADD_RECURRENCE',
        });
      },
      addLiveIn: async (newEvent, onSuccess=()=>undefined, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            backend.caregiver.addClient(newEvent.caregiverData.caregiverRn, {clientRn});
            backend.client.addCaregiver(clientRn, {caregiverRn: newEvent.caregiverData.caregiverRn, status: 'ACTIVE'});

            await backend.client.schedule.addLiveIn(clientRn, newEvent);

            setClientScheduleEvents({});
            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {
          debugString: 'CLIENT_SCHEDULE_ADD_LIVE_IN',
        });
      },
      deactivateRecurrence: async (recurrenceRn, deactivatedData, onSuccess=()=>undefined, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            const body = {
              deactivatedData,
            };
            await backend.client.schedule.deactivateRecurrence(clientRn, recurrenceRn, body);

            setClientScheduleEvents({});

            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {
          debugString: 'CLIENT_SCHEDULE_DEACTIVATE_RECURRENCE',
        });
      },
      deactivateLiveIn: async (liveInRn, deactivatedData, onSuccess=()=>undefined, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            const body = {
              deactivatedData,
            };
            await backend.client.schedule.deactivateLiveIn(clientRn, liveInRn, body);

            setClientScheduleEvents({});

            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {
          debugString: 'CLIENT_SCHEDULE_DEACTIVATE_LIVE_IN',
        });
      },
      cancelEvent: async (eventRn, reasonData, onSuccess=()=>undefined, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            await backend.event.cancelEvent(clientRn, eventRn, reasonData);

            setClientScheduleEvents({});
            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {
          debugString: 'CLIENT_SCHEDULE_CANCEL_EXCEPTION_RECURRENCE',
        });
      },
      loadMonthlyEvents: async (byMonths, loadCaregiverUpdates) => {
        progressWrap(async () => {
          const output = await backend.client.schedule.loadByMonth(clientRn, byMonths, loadCaregiverUpdates);

          const events = output.events.reduce((result, event) => {
            return {
              ...result,
              [event.eventRn]: event,
            };
          }, {});

          setClientScheduleEvents((e) => {
            return {
              ...e,
              ...events,
            };
          });
        }, {debugString: 'CLIENT_SCHEDULE_LOAD_EVENTS'});
      },
      rescheduleEvent: async (eventRn, eventData, reasonForException, onSuccess=()=>undefined) => {
        progressWrap(async () => {
          await backend.event.rescheduleEvent(clientRn, eventRn, reasonForException, eventData);

          setClientScheduleEvents({});
          onSuccess();
        }, {
          debugString: 'CLIENT_SCHEDULE_RESCHEDULE_EVENT',
        });
      },
    },
    careview: {
      addQuestion: async (type, questionData, onSuccess=()=>undefined, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            const {careview} = await backend.client.careview.addQuestion(clientRn, type, questionData);

            setClientCareviewSettings(careview);

            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {
          debugString: 'CLIENT_CAREVIEW_ADD_QUESTION',
        });
      },
      updateQuestion: async (questionRn, questionData, onSuccess=()=>undefined, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            const {careview} = await backend.client.careview.updateQuestion(clientRn, questionRn, questionData);

            setClientCareviewSettings(careview);

            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {
          debugString: 'CLIENT_CAREVIEW_UPDATE_QUESTION',
        });
      },
      deleteQuestion: async (questionRn, onSuccess=()=>undefined, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            const {careview} = await backend.client.careview.deleteQuestion(clientRn, questionRn);

            setClientCareviewSettings(careview);

            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {
          debugString: 'CLIENT_CAREVIEW_DELETE_QUESTION',
        });
      },
      resetQuestions: async (onSuccess=()=>undefined, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            const {careview} = await backend.client.careview.resetQuestions(clientRn);

            setClientCareviewSettings(careview);

            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {
          debugString: 'CLIENT_CAREVIEW_RESET_QUESTION',
        });
      },
      reorderQuestions: async (reorderList, onSuccess=()=>undefined, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            const {careview} = await backend.client.careview.reorderQuestion(clientRn, reorderList);

            setClientCareviewSettings(careview);

            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {
          debugString: 'CLIENT_CAREVIEW_REORDER_QUESTION',
        });
      },
    },
    payment: {
      loadClientHourlyRate: async () => {
        progressWrap(async () => {
          setClientPaymentPlanLoading(true);
          const output = await backend.clientPaymentPlan.loadClientPaymentPlan(clientRn);
          if (output.paymentPlan !== null) {
            setClientPaymentPlan(output.paymentPlan);
          } else {
            setClientPaymentPlan({});
          }
          setClientPaymentPlanLoading(false);
        }, {debugString: 'CLIENT_HOURLY_RATE_LOAD'});
      },

      updateClientHourlyRate: async (hourlyRate, onSuccess=()=>undefined, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            const output = await backend.clientPaymentPlan.updateClientHourlyRate(clientRn, hourlyRate);
            setClientPaymentPlan(output.paymentPlan);

            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {debugString: 'CLIENT_HOURLY_RATE_UPDATE'});
      },

      updateClientStripeId: async (stripeId, onSuccess=()=>undefined, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            const output = await backend.clientPaymentPlan.updateClientStripeId(clientRn, stripeId);
            setClientPaymentPlan(output.paymentPlan);

            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {debugString: 'CLIENT_STRIPE_ID_UPDATE'});
      },

      loadPaymentSlips: async () => {
        progressWrap(async () => {
          setClientPaymentSlipLoading(true);
          const output = await backend.paymentSlip.queryClientSlip(clientRn, clientLogsLastEvaluatedKey);

          setClientPaymentSlip((slip) => {
            const newSlip = output.list.reduce((result, log) => {
              return {
                ...result,
                [log.weekOf]: log,
              };
            }, {});

            return {
              ...slip,
              ...newSlip,
            };
          });
          setClientPaymentSlipLastEvaluatedKey(output.lastEvaluatedKey);
          setClientPaymentSlipLoading(false);
        }, {debugString: 'CLIENT_PAYMENT_SLIP_LOAD'});
      },

      markAsComplete: async (weekOf) => {
        progressWrap(async () => {
          const {slip} = await backend.paymentSlip.markClientSlipCompete(clientRn, weekOf);

          setClientPaymentSlip((slips) => {
            return {
              ...slips,
              [weekOf]: slip,
            };
          });
        }, {debugString: 'CLIENT_PAYMENT_SLIP_MARK_COMPLETE'});
      },
    },
    careplan: {
      updateCareplan: async (careplan) => {
        progressWrap(async () => {
          const output = await backend.client.updateCareplan(clientRn, careplan);
          setClientCareplan(output.careplan);
        }, {debugString: 'CLIENT_CAREPLAN_UPDATE'});
      },
    },
    user: {
      createUser: async (input) => {
        return progressWrap(async () => {
          setClientAuthenticationLoading(true);
          const result = await backend.user.createClient({
            email: input.email,
            firstName: input.firstName,
            lastName: input.lastName,
            clientRelationshipRn: input.clientRelationshipRn,
          });

          setClientAuthentication((auth) => ({
            ...auth,
            [result.user.userRn]: result.user,
          }));
          setClientAuthenticationLoading(false);
          return result.user;
        }, {debugString: 'CLIENT_CREATE_USER'});
      },

      deactivateUser: async (clientRelationshipRn, onSuccess=()=>undefined) => {
        return progressWrap(async () => {
          setClientAuthenticationLoading(true);
          const result = await backend.user.deactivateUser(clientRelationshipRn);
          setClientAuthentication((auth) => ({
            ...auth,
            [result.user.userRn]: result.user,
          }));
          onSuccess();
          setClientAuthenticationLoading(false);
          return result.user;
        }, {debugString: 'CLIENT_DEACTIVATE_USER'});
      },

      reactivateUser: async (clientRelationshipRn, onSuccess=()=>undefined) => {
        progressWrap(async () => {
          const result = await backend.user.reactivateUser(clientRelationshipRn);
          setClientAuthentication((auth) => {
            return {
              ...auth,
              [clientRelationshipRn]: result.user,
            };
          });
          onSuccess();
          setClientAuthenticationLoading(false);
          return result.user;
        }, {debugString: 'CLIENT_REACTIVATE_USER'});
      },

      getUser: async (userRn, loadAuthData, onSuccess=()=>undefined ) => {
        progressWrap(async () => {
          setClientAuthenticationLoading(true);
          const result = await backend.user.getUser(userRn, loadAuthData);
          setClientAuthentication((auth) => {
            return {
              ...auth,
              [userRn]: result.user,
            };
          });
          onSuccess();
          setClientAuthenticationLoading(false);
          return result.user;
        }, {debugString: 'CLIENT_GET_USER'});
      },
    },
    notification: {
      createUserEmail: async (userRn, email, onComplete=()=>undefined) => {
        progressWrap(async () => {
          try {
            setClientNotificationLoading(true);
            const result = await backend.notification.createUserEmail(userRn, email);
            setClientNotificationChannels((subscribedUsers) => {
              return {
                ...subscribedUsers,
                [result.userRn]: {
                  ...subscribedUsers[result.userRn],
                  emails: [...subscribedUsers[result.userRn].emails, result.userEmailData],
                },
              };
            });
            setClientNotificationLoading(false);
            return result;
          } finally {
            onComplete();
          }
        }, {debugString: 'CREATE_USER_EMAIL'});
      },
      removeUserEmail: async (userRn, email, onComplete=()=>undefined) => {
        progressWrap(async () => {
          try {
            setClientNotificationLoading(true);
            const result = await backend.notification.removeUserEmail(userRn, email);
            setClientNotificationChannels((subscribedUsers) => {
              const updated = {
                ...subscribedUsers,
                [result.userRn]: {
                  ...subscribedUsers[result.userRn],
                  emails: subscribedUsers[result.userRn].emails.filter((emailData) => emailData.email !== email),
                },
              };
              return updated;
            });
            setClientNotificationLoading(false);
            return result;
          } finally {
            onComplete();
          }
        }, {debugString: 'REMOVE_USER_EMAIL'});
      },
      createUserSms: async (userRn, phoneNumber, onComplete=()=>undefined) => {
        progressWrap(async () => {
          try {
            setClientNotificationLoading(true);
            const result = await backend.notification.createUserSms(userRn, phoneNumber);
            setClientNotificationChannels((subscribedUsers) => ({
              ...subscribedUsers,
              [result.userRn]: {
                ...subscribedUsers[result.userRn],
                smsNumbers: [...subscribedUsers[result.userRn].smsNumbers, result.userSmsData],
              },
            }));
            setClientNotificationLoading(false);
            return result;
          } finally {
            onComplete();
          }
        }, {debugString: 'CREATE_USER_SMS'});
      },
      removeUserSms: async (userRn, phoneNumber, onComplete=()=>undefined) => {
        progressWrap(async () => {
          try {
            setClientNotificationLoading(true);
            const result = await backend.notification.removeUserSms(userRn, phoneNumber);
            setClientNotificationChannels((subscribedUsers) => {
              const updated = {
                ...subscribedUsers,
                [result.userRn]: {
                  ...subscribedUsers[result.userRn],
                  smsNumbers: subscribedUsers[result.userRn].smsNumbers.filter((smsData) => smsData.phoneNumber !== phoneNumber),
                },
              };
              return updated;
            });
            setClientNotificationLoading(false);
            return result;
          } finally {
            onComplete();
          }
        }, {debugString: 'REMOVE_USER_SMS'});
      },
      getChannels: async (userRn, onSuccess=()=>undefined) => {
        progressWrap(async () => {
          setClientNotificationLoading(true);
          const result = await backend.notification.getChannels(userRn);
          setClientNotificationChannels((subscribedUsers) => ({
            ...subscribedUsers,
            [result.userRn]: {
              emails: result.userEmails ?? [],
              smsNumbers: result.userSmsNumbers ?? [],
            },
          }));
          onSuccess();
          setClientNotificationLoading(false);
          return result;
        }, {debugString: 'GET_CHANNELS'});
      },
    },
    communication: {
      createSmsSubscription: async (userRn, phoneNumber, onComplete=()=>undefined) => {
        progressWrap(async () => {
          try {
            setClientCommunicationLoading(true);
            const result = await backend.communication.createSmsSubscription(userRn, phoneNumber);
            setClientCommunication((clientCommunication) => ({
              ...clientCommunication,
              [userRn]: result.subscription,
            }));
            setClientCommunicationLoading(false);
            return result;
          } finally {
            onComplete();
          }
        }, {debugString: 'CREATE_COMMUNICATION_SMS_SUBSCRIPTION'});
      },
      getSmsSubscription: async (userRn, onSuccess=()=>undefined) => {
        progressWrap(async () => {
          setClientCommunicationLoading(true);
          const result = await backend.communication.getSmsSubscription(userRn);
          setClientCommunication((clientCommunication) => {
            return {
              ...clientCommunication,
              [userRn]: result.subscription ?? null,
            };
          });
          onSuccess();
          setClientCommunicationLoading(false);
          return result.subscription;
        }, {debugString: 'GET_COMMUNICATION_SMS_SUBSCRIPTION'});
      },
      updateSmsSubscription: async (userRn, phoneNumber, newSubscriptionStatus, onComplete=()=>undefined) => {
        progressWrap(async () => {
          try {
            setClientCommunicationLoading(true);
            const result = await backend.communication.updateSmsSubscription(userRn, phoneNumber, newSubscriptionStatus);
            setClientCommunication((clientCommunication) => ({
              ...clientCommunication,
              [userRn]: result.subscription,
            }));
            setClientCommunicationLoading(false);
            return result.subscription;
          } finally {
            onComplete();
          }
        }, {debugString: 'UPDATE_COMMUNICATION_SMS_SUBSCRIPTION'});
      },
    },
    dialpadIntegration: {
      createDialpadLink: async (clientRn, clientRelationshipRn, phoneNumber, onComplete=()=>undefined) => {
        progressWrap(async () => {
          try {
            setClientDialpadIntegrationLoading(true);
            const result = await backend.dialpadIntegration.createDialpadLink(clientRn, phoneNumber);
            setClientDialpadIntegration((dialpadIntegration) => (
              Object.fromEntries(
                  Object.entries(dialpadIntegration).map(([key, value]) => [
                    key,
                    {
                      phoneNumber: key === clientRelationshipRn ? result.phoneNumber : 'CLIENT_ALREADY_REGISTERED',
                    },
                  ]),
              )
            ));
            setClientDialpadIntegrationLoading(false);
            return result;
          } finally {
            onComplete();
          }
        }, {debugString: 'CREATE_DIALPAD_INTEGRATION'});
      },
      getDialpadLink: async (clientRn, userRn, onSuccess=()=>undefined) => {
        progressWrap(async () => {
          setClientDialpadIntegrationLoading(true);
          const result = await backend.dialpadIntegration.getDialpadLink(clientRn);
          setClientDialpadIntegration((dialpadIntegration) => {
            // try and find out whose phone number it is
            const matchedRelationshipRn = Object.entries(clientData?.relationships ?? {}).find(([, detail]) => {
              return detail?.contacts?.some((contactData) => (
                contactData?.type === 'Phone' && contactData?.detail === result.phoneNumber
              ));
            })?.[0];
            const isMatchForUser = matchedRelationshipRn === userRn;
            if (matchedRelationshipRn) {
              return {
                ...dialpadIntegration,
                [userRn]: {phoneNumber: isMatchForUser ? result.phoneNumber : 'CLIENT_ALREADY_REGISTERED'},
              };
            } else {
              return {
                ...dialpadIntegration,
                [userRn]: {phoneNumber: null},
              };
            }
          });
          onSuccess();
          setClientDialpadIntegrationLoading(false);
          return result.phoneNumber;
        }, {debugString: 'GET_DIALPAD_INTEGRATION'});
      },
    },
  };

  if (!clientDataLoaded) return null;

  return (
    <>
      <Grid container style={{'textAlign': 'left'}}>
        <Grid item md={9} xs={12} >
          <ClientFeed
            clientRn={clientRn}
            feedTab={feedTab}
            setFeedTab={setFeedTab}
            clientData={clientData}
            clientMessages={clientMessages}
            clientLogs={clientLogs}
            clientScheduleEvents={clientScheduleEvents}
            clientForms={clientForms}
            clientCareviewSettings={clientCareviewSettings}
            clientBackend={clientBackend}
            refresh={refresh}
            setRefresh={setRefresh}
            showSettings={showSettings}
            setOpenDrawerView={setOpenDrawerView}
            clientMessagesLoading={clientMessagesLoading}
            clientMessagesLastEvaluatedKey={clientMessagesLastEvaluatedKey}
            clientLogsLastEvaluatedKey={clientLogsLastEvaluatedKey}
            clientLogsLoading={clientLogsLoading}
            clientPaymentSlip={clientPaymentSlip}
            clientPaymentSlipLastEvaluatedKey={clientPaymentSlipLastEvaluatedKey}
            clientPaymentSlipLoading={clientPaymentSlipLoading}
            clientPaymentPlan={clientPaymentPlan}
            clientCareplan={clientCareplan}
            clientPaymentPlanLoading={clientPaymentPlanLoading}
            clientAuthentication={clientAuthentication}
            clientAuthenticationLoading={ClientAuthenticationLoading}
            clientNotificationChannels={clientNotificationChannels}
            clientNotificationLoading={clientNotificationLoading}
            clientCommunication={clientCommunication}
            clientCommunicationLoading={clientCommunicationLoading}
            clientDialpadIntegration={clientDialpadIntegration}
            clientDialpadIntegrationLoading={clientDialpadIntegrationLoading}
            clientCallStatus={clientCallStatus}
            clientOwnerData={clientOwnerData}
          />
        </Grid>
        <Grid item md={3} pl={{md: 2, xs: 1}}>
          <ClientSettings
            showSettings={showSettings}
            openDrawerView={openDrawerView}
            setOpenDrawerView={setOpenDrawerView}
            clientSettingsView={clientSettingsView}
            setClientSettingsView={setClientSettingsView}
            clientRn={clientRn}
            clientData={clientData}
            clientBackend={clientBackend}
          />
        </Grid>
      </Grid>
    </>
  );
};

ClientView.propTypes = {
  clientRn: PropTypes.string.isRequired,
  statusProgressWrap: PropTypes.func.isRequired,
  requester: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  requester: state.user,
});

export default connect(mapStateToProps, dispatch)(React.memo(ClientView));
